import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private apiUrl = environment.api;
  private filesSubject = new BehaviorSubject<any[]>([]);

  constructor(private http: HttpClient) {
    this.loadFiles();
  }

  getAllFiles(): Observable<any[]> {
    return this.http.get<any>(`${this.apiUrl}/files`)
      .pipe(
        tap(response => {
          console.log('Get files response:', response);
          const filesArray = response.data || response;
          
          const mappedFiles = filesArray.map((file: any) => ({
            id: file.id,
            title: file.title || file.original_name,
            originalName: file.original_name,
            mimeType: file.mime_type,
            createdAt: file.created_at,
            description: file.description || '',
            linkUrl: file.linkUrl || file.link_url,
            fileType: file.fileType || file.file_type || this.getFileTypeFromMimeType(file.mime_type),
            category: this.getCategoryFromMimeType(file.mime_type),
            mainFileName: file.original_name,
            uploadDate: new Date(file.created_at)
          }));
          this.filesSubject.next(mappedFiles);
        }),
        catchError(this.handleError)
      );
  }

  getFilesStream(): Observable<any[]> {
    return this.filesSubject.asObservable();
  }

  private loadFiles(): void {
    this.getAllFiles().subscribe({
      error: error => {
        console.error('Failed to load files:', error);
      }
    });
  }

  uploadFiles(formData: FormData): Observable<any> {
    console.log('Uploading file to:', `${this.apiUrl}/files/upload`);
    console.log('FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    
    return this.http.post<any>(`${this.apiUrl}/files/upload`, formData)
      .pipe(
        tap(response => {
          console.log('Upload response:', response);
          const fileData = response.data || response;
          
          const newFile = {
            id: fileData.id,
            title: fileData.original_name,
            originalName: fileData.original_name,
            mimeType: fileData.mime_type,
            createdAt: fileData.created_at,
            description: fileData.description || '',
            category: this.getCategoryFromMimeType(fileData.mime_type),
            mainFileName: fileData.original_name,
            uploadDate: new Date(fileData.created_at)
          };
          
          const currentFiles = this.filesSubject.value;
          this.filesSubject.next([newFile, ...currentFiles]);
        }),
        catchError(this.handleError)
      );
  }

  uploadLink(linkData: any): Observable<any> {
    console.log('Uploading link to:', `${this.apiUrl}/files/upload`);
    console.log('Link data:', linkData);
    
    // Create a FormData with link information
    const formData = new FormData();
    
    // Create a minimal 1x1 PNG image as placeholder (much cleaner than PDF)
    const pngData = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==';
    const binaryString = atob(pngData);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'image/png' });
    const file = new File([blob], `${linkData.title}.png`, { type: 'image/png' });
    
    formData.append('file', file);
    formData.append('original_name', `${linkData.title}.png`);
    formData.append('stored_name', `${linkData.title}.png`);
    formData.append('mime_type', 'image/png');
    formData.append('path', '/uploads/');
    formData.append('description', linkData.description || '');
    formData.append('linkUrl', linkData.linkUrl);
    formData.append('fileType', linkData.fileType || 'linkfile');
    
    console.log('=== LINK UPLOAD DEBUG ===');
    console.log('File Type:', linkData.fileType);
    console.log('Form Values:', {
      title: linkData.title,
      description: linkData.description,
      linkUrl: linkData.linkUrl
    });
    console.log('Uploading file to:', `${this.apiUrl}/files/upload`);
    console.log('FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    console.log('==========================');
    
    return this.http.post<any>(`${this.apiUrl}/files/upload`, formData)
      .pipe(
        tap(response => {
          console.log('Link upload response:', response);
          const fileData = response.data || response;
          
          const newFile = {
            id: fileData.id,
            title: linkData.title,
            originalName: linkData.title,
            mimeType: 'application/link',
            createdAt: fileData.created_at,
            description: linkData.description || '',
            linkUrl: linkData.linkUrl,
            fileType: 'linkfile',
            category: 'link',
            mainFileName: linkData.title,
            uploadDate: new Date(fileData.created_at)
          };
          
          const currentFiles = this.filesSubject.value;
          this.filesSubject.next([newFile, ...currentFiles]);
        }),
        catchError(this.handleError)
      );
  }

  updateLink(fileId: string, linkData: any): Observable<any> {
    console.log('Updating link:', fileId, linkData);
    
    const payload = {
      title: linkData.title,
      description: linkData.description,
      linkUrl: linkData.linkUrl,
      fileType: linkData.fileType
    };
    
    return this.http.put<any>(`${this.apiUrl}/files/${fileId}`, payload)
      .pipe(
        tap(response => {
          console.log('Link update response:', response);
          const fileData = response.data || response;
          
          const currentFiles = this.filesSubject.value;
          const updatedFiles = currentFiles.map(file => 
            file.id === fileId ? {
              ...file,
              title: linkData.title,
              description: linkData.description,
              linkUrl: linkData.linkUrl
            } : file
          );
          this.filesSubject.next(updatedFiles);
        }),
        catchError(this.handleError)
      );
  }

  deleteFile(fileId: string): Observable<any> {
    return this.http.delete<void>(`${this.apiUrl}/files/${fileId}`)
      .pipe(
        tap(() => {
          const currentFiles = this.filesSubject.value;
          const updatedFiles = currentFiles.filter(file => file.id !== fileId);
          this.filesSubject.next(updatedFiles);
        }),
        catchError(this.handleError)
      );
  }

  downloadFile(fileId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/files/download/${fileId}`, { responseType: 'blob' })
      .pipe(
        catchError(this.handleError)
      );
  }

  private getCategoryFromMimeType(mimeType: string): string {
    if (mimeType.includes('pdf')) return 'documentation';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'documentation';
    if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'software';
    if (mimeType.includes('image')) return 'resources';
    if (mimeType === 'application/link') return 'link';
    return 'other';
  }

  private getFileTypeFromMimeType(mimeType: string): string {
    if (mimeType === 'application/link') return 'linkfile';
    return 'rawfile';
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('File service error:', error);
    console.error('Error status:', error.status);
    console.error('Error message:', error.message);
    console.error('Error body:', error.error);
    return throwError(() => error);
  }
}